import { getMatchId } from '../pages/tourDetails/TournamentPlay/components/Match/Match.helpers';
import type {
  IMatch,
  PlayValues,
} from '../pages/tourDetails/TournamentPlay/components/PlayTag/PlayTag';

export type MatchSortAssignment = {
  id: IMatch['id'];
  sortKey: number;
};

export type MatchRenumberUpdate = {
  id: IMatch['id'];
  sortKey?: number;
  matchId?: string;
  homeTeamPlaceholder?: string;
  awayTeamPlaceholder?: string;
};

type BuildMatchRenumberOptions = {
  skipMatchId: (match: IMatch) => boolean;
};

function groupIdOf(match: IMatch): number | null {
  return match.tournamentGroup?.id ?? match.tournamentGroupId ?? null;
}

function nextMatchId(match: IMatch, sortKey: number): string {
  const groupId = groupIdOf(match);
  return getMatchId(sortKey, groupId != null, groupId, match.type, match.isGold);
}

function remapPlaceholder(
  value: string | null,
  idMap: ReadonlyMap<string, string>
): string | undefined {
  if (!value) {
    return undefined;
  }
  const next = idMap.get(value);
  if (next === undefined || next === value) {
    return undefined;
  }
  return next;
}

export function buildMatchRenumberUpdates(
  matches: PlayValues['tournamentMatches'],
  orderedIds: MatchSortAssignment[],
  opts: BuildMatchRenumberOptions
): MatchRenumberUpdate[] {
  const matchesById = new Map<string, IMatch>();
  for (const match of Object.values(matches)) {
    if (match?.id == null || match.id === '') {
      continue;
    }
    matchesById.set(String(match.id), match);
  }

  const sortKeyById = new Map<string, number>();
  for (const item of orderedIds) {
    sortKeyById.set(String(item.id), item.sortKey);
  }

  const idMap = new Map<string, string>();
  for (const item of orderedIds) {
    const match = matchesById.get(String(item.id));
    if (!match || opts.skipMatchId(match)) {
      continue;
    }
    const oldMatchId = match.matchId;
    if (!oldMatchId) {
      continue;
    }
    const newMatchId = nextMatchId(match, item.sortKey);
    if (oldMatchId !== newMatchId) {
      idMap.set(oldMatchId, newMatchId);
    }
  }

  const updates: MatchRenumberUpdate[] = [];
  const seen = new Set<string>();

  const collect = (match: IMatch, sortKey: number | undefined) => {
    const key = String(match.id);
    if (seen.has(key)) {
      return;
    }
    seen.add(key);

    const update: MatchRenumberUpdate = { id: match.id };
    let changed = false;

    if (sortKey != null && sortKey !== match.sortKey) {
      update.sortKey = sortKey;
      changed = true;
    }

    if (sortKey != null && !opts.skipMatchId(match)) {
      const newMatchId = nextMatchId(match, sortKey);
      if (match.matchId !== newMatchId) {
        update.matchId = newMatchId;
        changed = true;
      }
    }

    const homeTeamPlaceholder = remapPlaceholder(match.homeTeamPlaceholder, idMap);
    if (homeTeamPlaceholder !== undefined) {
      update.homeTeamPlaceholder = homeTeamPlaceholder;
      changed = true;
    }
    const awayTeamPlaceholder = remapPlaceholder(match.awayTeamPlaceholder, idMap);
    if (awayTeamPlaceholder !== undefined) {
      update.awayTeamPlaceholder = awayTeamPlaceholder;
      changed = true;
    }

    if (changed) {
      updates.push(update);
    }
  };

  for (const item of orderedIds) {
    const match = matchesById.get(String(item.id));
    if (match) {
      collect(match, item.sortKey);
    }
  }
  for (const match of matchesById.values()) {
    if (!sortKeyById.has(String(match.id))) {
      collect(match, undefined);
    }
  }

  return updates;
}
